
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useIngestionSources } from '@/hooks/useIngestionSources';
import type { BillStage } from '@/lib/stage-utils';
import { MultiSourceManager } from '@/components/MultiSourceManager';
import { ProcessingSourcesPanel } from './processing/ProcessingSourcesPanel';
import { ParserConfigPanel } from './processing/ParserConfigPanel';
import { ManualReviewGuidelines } from './manual-review/ManualReviewGuidelines';
import { ValidationRulesLibrary } from './validation/ValidationRulesLibrary';
import { ApprovalHierarchy } from './approval/ApprovalHierarchy';
import { ExportTemplates } from './payment-ready/ExportTemplates';
import { ArchiveAnalytics } from './archive/ArchiveAnalytics';

interface StageSourcesPanelProps {
  stage: BillStage;
}

const SOURCE_TYPES = ['manual', 'email', 'bulk', 'portal', 'drive', 'sftp', 'api'] as const;

type SourceType = (typeof SOURCE_TYPES)[number];

export function StageSourcesPanel({ stage }: StageSourcesPanelProps) {
  const [sourceType, setSourceType] = useState<SourceType>('manual');
  const [isParserConfigOpen, setIsParserConfigOpen] = useState(false);
  const { sources, isLoading, createSource, updateSourceStatus } = useIngestionSources(stage);

  // For intake stage, show the new multi-source manager as primary interface
  if (stage === 'intake') {
    return <MultiSourceManager />;
  }

  // For processing stage, show new ProcessingSourcesPanel
  if (stage === 'processing') {
    return (
      <div className="space-y-6">
        <ProcessingSourcesPanel />
        
        <Collapsible open={isParserConfigOpen} onOpenChange={setIsParserConfigOpen}>
          <Card>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">AI Parser Configuration (Advanced)</CardTitle>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isParserConfigOpen ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <ParserConfigPanel />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    );
  }

  // Manual Review stage - show guidelines
  if (stage === 'manual_review') {
    return <ManualReviewGuidelines />;
  }

  // Validation stage - show rules library
  if (stage === 'validation') {
    return <ValidationRulesLibrary />;
  }

  // Approval stage - show approval hierarchy
  if (stage === 'approval') {
    return <ApprovalHierarchy />;
  }

  // Payment Ready stage - show export templates
  if (stage === 'payment_ready') {
    return <ExportTemplates />;
  }

  // Archive stage - show analytics
  if (stage === 'archive') {
    return <ArchiveAnalytics />;
  }

  // For other stages, show the legacy source management interface
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Connect or Add Sources</CardTitle>
          <CardDescription>Configure where bills come from in this stage.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:w-64">
            <Select value={sourceType} onValueChange={(v) => setSourceType(v as SourceType)}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a source" />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={async () => {
              await createSource({ stage, source_type: sourceType });
            }}
          >
            Add Source
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Connections</CardTitle>
          <CardDescription>Manage status and retry connections.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading sources…</div>
          ) : sources.length === 0 ? (
            <div className="text-sm text-muted-foreground">No sources yet. Add one above.</div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sources.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <div className="font-medium">{s.source_type.toUpperCase()}</div>
                    <div className="text-xs text-muted-foreground">Updated {new Date(s.updated_at || s.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={s.status === 'active' ? 'default' : s.status === 'error' ? 'destructive' : 'secondary'}>
                      {s.status}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await updateSourceStatus({ id: s.id, status: s.status === 'active' ? 'inactive' : 'active' });
                      }}
                    >
                      {s.status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
